/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, OnInit, OnDestroy} from '@angular/core';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {Subject} from 'rxjs';
import {ReportProvider} from '../../../../../providers';
import {ClientUser, Report, ReportEngageResult, Helpers} from '../../../../../model';
import {ReportService} from '../../../../../services/report.service';
import { } from 'file-saver/dist/FileSaver';
import {StageService, ModeService} from '../../../../../services';
import * as moment from 'moment';
import { SystemType } from 'src/app/model/enums/system.type';
import { PreviewSize } from 'src/app/model/enums/preview.size';
import { ConfirmationDialogComponent } from 'src/app/components/fields';
import { LogService } from 'src/app/services';

@Component({
  selector: 'engage-report-modal',
  templateUrl: './engage-report.modal.html',
  styleUrls: ['./engage-report.modal.scss']
})
export class EngageReportModal implements OnInit, OnDestroy {
  subject: Subject<boolean>;
  subscription: any;
  isResend: boolean;

  currentReport: Report;
  private reportSub: any;

  isLoading = false;
  isLoadingPreview = false;

  preview: BsModalRef;
  currentMode: SystemType;
  modeSub: any;
  PreviewSize = PreviewSize;
  size: PreviewSize = PreviewSize.MEDIUM;
  confirmRef: BsModalRef;


  constructor(
    private modalService: BsModalService,
    private reportProvider: ReportProvider,
    private reportService: ReportService,
    private stageService: StageService,
    private modeService: ModeService,
    private logService : LogService
  ) {}

  ngOnInit(): void {
    // Fix for when user clicks `ESC` instead of clicking, no Event was firing
    this.subscription = this.modalService.onHide.subscribe((reason: string) => {
      if (reason === 'esc') {
        if (!this.isLoading)
          this.onCancel();
      }
    });

    this.reportSub = this.reportService.report$.subscribe(currentReport => this.currentReport = currentReport);

    this.modeSub = this.modeService.mode$.subscribe(mode=> this.currentMode = mode);
  }

  ngOnDestroy(): void {
    if (this.subscription)
      this.subscription.unsubscribe();

    if (this.reportSub)
      this.reportSub.unsubscribe();

    if(this.modeSub)
      this.modeSub.unsubscribe();
  }

  onConfirm(): void {
    this.isLoading = true;
    if(this.isResend){
      this.reportProvider.resendDs(this.currentReport.ID, this.currentMode)
      .subscribe(data => {
        this.isLoading = false;
        if (data.success) {

          this.showSuccessDialog();

        } else this.logService.error(JSON.stringify(data, null, 2));
      }, error => {
        this.isLoading = false;
        this.logService.error(error);
      });
    }
    else {
      this.reportProvider.engageReport(this.currentReport.ID, this.currentMode)
      .subscribe(data => {
        this.isLoading = false;
        if (data.success) {
          const engageResult = ReportEngageResult.parse(data.result);

          this.reportService.addStageHistory(engageResult.HISTORY_STAGE);
          this.currentReport.STAGE = this.stageService.getStageById(engageResult.HISTORY_STAGE.SID);
          this.currentReport.ASSIGNEE = engageResult.HISTORY_STAGE.ASSIGNEE;
          this.currentReport.STATE = engageResult.HISTORY_STAGE.STATE;

          /* this.currentReport.DOCUMENT_ID = engageResult.DOCUMENT_ID;
          this.currentReport.DOCUMENT_STATE = engageResult.DOCUMENT_STATE; */
          this.currentReport.DATE_ENGAGED_LETTER = moment(engageResult.DATE_ENGAGED_LETTER).format('MMM DD, YYYY');
          this.currentReport.DATE_ENGAGED = moment(engageResult.DATE_ENGAGED).format('MMM DD, YYYY');

          this.showSuccessDialog();

        } else this.logService.error(JSON.stringify(data, null, 2));
      }, error => {
        this.isLoading = false;
        this.logService.error(error);
      });
    }
  }

  onCancel(): void {
    if (!this.isLoading) {
      this.subject.next(false);
      this.subject.complete();
    }
  }

  getSigner(): ClientUser {
    if (this.currentReport && this.currentReport.CLIENT_USERS) {
      for (const user of this.currentReport.CLIENT_USERS) {
        if (user.EMAIL_ENGAGE)
          return user.CLIENT_USER;
      }
    }

    return null;
  }

  blobToUint8Array(b): any {
    const uri = URL.createObjectURL(b);
    const xhr = new XMLHttpRequest();
    let i, ui8;

    xhr.open('GET', uri, false);
    xhr.send();

    URL.revokeObjectURL(uri);

    ui8 = new Uint8Array(xhr.response.length);

    for (i = 0; i < xhr.response.length; ++i) {
      ui8[i] = xhr.response.charCodeAt(i);
    }

    return ui8;
  }

  expandArea(): void{
    if(this.size === PreviewSize.MEDIUM){
      this.size = PreviewSize.LARGE;
    } else {
      this.size = PreviewSize.MEDIUM;
    }
  }

  showSuccessDialog(): void {
    const config = Helpers.getModalConfig();
    config.class = 'modal-dialog-centered';

    this.confirmRef = this.modalService.show(ConfirmationDialogComponent, config);
    const ackSubject = new Subject<any>();
    this.confirmRef.content.subject = ackSubject;
    this.confirmRef.content.title = 'Success';
    this.confirmRef.content.text = 'Your email has been successfully sent!';
    this.confirmRef.content.actionButton = 'Ok';
    this.confirmRef.content.showCancel = false;

    ackSubject.subscribe(value => {
      if(value){
        this.subject.next(true);
        this.subject.complete();
      }
    });
  }
}
